import { useState } from 'react';
import { Container } from 'react-bootstrap';
import ModelList from '../../components/components-model/ModelList';
import ModelForm from '../../components/components-model/ModelForm';
import ModelProfileManager from '../../components/components-model/ModelProfileManager';
import { useFetch } from '../../hooks/useFetch';
import { useDocumentMeta } from '../../hooks/useDocumentMeta';
import {
    fetchCreateModelService,
    fetchDeleteModelService,
    fetchModelService,
    fetchRefresTokenObsModelService,
    fetchUpdateModelAccountService,
    fetchUploadFileModelService,
} from '../../services/modelsService';
import './admin.css';

/**
 * Gestión de modelos del estudio.
 *
 * Los servicios devuelven ya el contenido de la respuesta y lanzan si algo va
 * mal; antes esta pantalla desenvolvía `result.data` a mano en seis sitios y
 * comprobaba `if (!result?.data?.id)` para adivinar si había fallado, porque
 * un error llegaba como `null` y era indistinguible de una respuesta vacía.
 */
export default function AdminModels() {
    useDocumentMeta({ title: 'Modelos', noIndex: true });

    // per_page alto: el panel muestra el catálogo completo, no una página.
    const { data: models, setData: setModels, error } = useFetch(fetchModelService, {
        params: { per_page: 100 },
        global: true,
    });

    const [file, setFile] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [currentModel, setCurrentModel] = useState(null);
    const [profileModel, setProfileModel] = useState(null);
    const [formError, setFormError] = useState(null);
    const [status, setStatus] = useState(null);

    const handleCreate = () => {
        setCurrentModel(null);
        setFormError(null);
        setShowModal(true);
    };

    const handleEdit = (model) => {
        // Sin limpiar el error, el aviso del intento anterior seguía en pantalla
        // al abrir el formulario de otra modelo.
        setFormError(null);
        setCurrentModel({
            ...model,
            name: model?.user?.name,
            email: model?.user?.email,
        });
        setShowModal(true);
    };

    // Cerrar sin guardar debe descartar también la foto elegida: si no, la
    // siguiente modelo que se editara heredaba el archivo del intento anterior.
    const closeModal = () => {
        setShowModal(false);
        setFile(null);
        setFormError(null);
    };

    const handleProfileSaved = (updated) => {
        if (!updated) return;

        setModels((prev) => prev?.map((model) => (model.id === updated.id ? { ...model, ...updated } : model)));
        // También la copia que tiene abierta el modal: si no, seguía mostrando
        // la portada y los datos con los que se abrió.
        setProfileModel((prev) => (prev && prev.id === updated.id ? { ...prev, ...updated } : prev));
    };

    const handleDelete = async (id) => {
        if (!window.confirm('¿Dar de baja a esta modelo? Se le retirará el rol y su perfil dejará de publicarse.')) {
            return;
        }

        try {
            const { id: eliminado } = await fetchDeleteModelService(id);
            setModels((prev) => prev.filter((model) => model.id !== eliminado));
            setStatus('Modelo dada de baja.');
        } catch (err) {
            setFormError(err.message);
        }
    };

    /** Sube la foto si hay una elegida y devuelve la modelo ya actualizada. */
    const uploadAvatar = async (id) => {
        if (!file || !id) return null;

        return await fetchUploadFileModelService(id, file);
    };

    const handleSubmit = async (data) => {
        setFormError(null);

        try {
            if (currentModel) {
                // El correo no viaja: es la identidad de acceso y el backend
                // tampoco lo acepta. Se editan nombre, foto y contraseña.
                const payload = { name: data.name };
                if (data.password) payload.password = data.password;

                const cuenta = await fetchUpdateModelAccountService(currentModel.id, payload);
                // La subida responde con la modelo ya actualizada. Antes se
                // guardaba en la lista la respuesta *anterior* a la subida, así
                // que la foto nueva no aparecía hasta recargar la página.
                const actualizado = (await uploadAvatar(currentModel.id)) ?? cuenta;

                setModels((prev) => prev.map((model) => (model.id === currentModel.id ? actualizado : model)));
                setStatus('Modelo actualizada.');
            } else {
                const creada = await fetchCreateModelService(data);
                const conFoto = (await uploadAvatar(creada.id)) ?? creada;

                setModels((prev) => [conFoto, ...(prev || [])]);
                setStatus('Modelo registrada.');
            }

            setFile(null);
            setShowModal(false);
        } catch (err) {
            setFormError(err.message);
        }
    };

    const refresStreamingToken = async (id) => {
        try {
            const { streaming_token } = await fetchRefresTokenObsModelService(id);
            setModels((prev) => prev.map((model) => (model.id === id ? { ...model, streaming_token } : model)));
            setStatus('Clave de emisión rotada. Hay que reconfigurar OBS con la nueva.');
        } catch (err) {
            setFormError(err.message);
        }
    };

    return (
        <div className="admin-container">
            <Container fluid>
                <div className="admin-header">
                    <div>
                        <h2 className="admin-title">Modelos</h2>
                        <p className="text-muted small mb-0">
                            Cuentas del estudio, perfiles públicos y claves de emisión
                        </p>
                    </div>
                    <button className="btn-create-model" onClick={handleCreate}>
                        <i className="fa-solid fa-plus" aria-hidden="true"></i>
                        <span>Nueva modelo</span>
                    </button>
                </div>

                {(error || formError) && (
                    <div className="alert alert-danger py-2 small">{error || formError}</div>
                )}

                {status && <div className="alert alert-success py-2 small">{status}</div>}

                <ModelList
                    models={models}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onProfile={setProfileModel}
                    refresStreamingToken={refresStreamingToken}
                />

                <ModelForm
                    show={showModal}
                    onHide={closeModal}
                    onSubmit={handleSubmit}
                    model={currentModel}
                    setFile={setFile}
                    error={formError}
                />

                <ModelProfileManager
                    show={Boolean(profileModel)}
                    onHide={() => setProfileModel(null)}
                    model={profileModel}
                    onSaved={handleProfileSaved}
                />
            </Container>
        </div>
    );
}
